import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import './TestHistory.scss';
// import Footer from '../../../Components/Footer/Footer';
import TestHistoryVid from '../../../assets/videos/Swimscore-doctor.mp4';
import Service from '../Dashboard/services/Service';

const TestHistory = () => {
  const navigate = useNavigate();
  const { user: loggedUser } = useSelector((state) => state.auth);
  const [activeSlide, setActiveSlide] = useState(0);
  const [testHistory, setTestHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const userId = loggedUser?.id;

  // Fetch test history data
  useEffect(() => {
    const fetchTestHistory = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        const token = sessionStorage.getItem('auth_token');
        const headers = { Authorization: `Bearer ${token}` };
        const response = await Service.getOrderList(userId, headers);
        const orders = response?.data || [];

        if (orders.length > 0) {
          const allTestHistory = [];
          const processedOrders = new Set();

          orders.forEach((order) => {
            const orderId = order?.db_order?.id || order?.id;
            const orderNumber = order?.order_number;

            if (processedOrders.has(orderId)) {
              return;
            }

            const overallScore = order?.db_order?.swimscore_overall_score;
            const orderDate = order?.db_order?.created_at;
            const uiMetrics = order?.db_order?.ui_metrics || [];

            // Check if order has test data (either ui_metrics or swimscore)
            if (overallScore !== null && overallScore !== undefined) {
              allTestHistory.push({
                date: orderDate,
                score: overallScore,
                orderId: orderId,
                orderNumber: orderNumber,
                orderCreatedAt: orderDate,
                uiMetrics: uiMetrics,
                email: order?.email,
                financial_status: order?.financial_status,
                order_status: order?.db_order?.tasso_status_detail || order?.db_order?.mailman_status_detail,
              });
              processedOrders.add(orderId);
            }
          });

          // Sort by order date (newest first) - use db_order.created_at for sorting
          allTestHistory.sort((a, b) => {
            const dateA = new Date(a.orderCreatedAt || a.date);
            const dateB = new Date(b.orderCreatedAt || b.date);
            return dateB - dateA;
          });

          // Show ALL orders with test data (no duplicate filtering, no 12-month limit)
          // But limit display to 4 cards for UI (user can scroll)
          setTestHistory(allTestHistory);
        } else {
          setTestHistory([]);
        }
      } catch (error) {
        console.error('❌ Failed to fetch test history:', error);
        setTestHistory([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTestHistory();
  }, [userId]);

  const totalSlides = Math.max(testHistory.length, 1);

  const handleCardClick = (test) => {
    if (test?.orderId || test?.orderNumber) {
      // Navigate to dashboard with order data
      navigate('/dashboard', {
        state: {
          orderId: test.orderId,
          orderNumber: test.orderNumber,
        },
      });
    }
  };

  const calculateDashOffset = (score) => {
    if (score === null || score === undefined) return 534;
    return 534 * (1 - score / 100);
  };

  return (
    <div className="testhistory-page">
      <div className="container">
        <h3 className="section-title">Test History</h3>
        {!loading && testHistory.length > 0 && (
          <p className="section-subtitle">
            The latest results look good, you likely have strong fertility potential at the moment.
            <br />
            Your sperm parameters indicate healthy reproductive function.
          </p>
        )}
        {!loading && testHistory.length === 0 && <p className="section-subtitle">No test history available</p>}

        <div className="last-month-block">
          <h4 className="title">Last 12 months</h4>

          <div className="score-row">
            {loading ? (
              <div className="loading-state">Loading test history...</div>
            ) : testHistory.length === 0 ? (
              <div className="empty-state">
                <p className="empty-state__message">No test history available. Order your first test to get started!</p>
                <button className="btn btn-primary empty-state__cta" onClick={() => navigate('/order-next-test')}>
                  Order your first test
                </button>
              </div>
            ) : (
              <>
                <div className="score-track" style={{ transform: `translateX(-${activeSlide * 100}%)` }}>
                  {testHistory.map((test, index) => (
                    <div
                      key={index}
                      className={`score-card ${index === activeSlide ? 'score-card--active' : ''}`}
                      onClick={() => handleCardClick(test)}
                      style={{ cursor: test.orderId || test.orderNumber ? 'pointer' : 'default' }}
                    >
                      <div className="score-header">
                        <h6 className="score-date">
                          {test.orderCreatedAt ? format(new Date(test.orderCreatedAt), 'dd MMMM yyyy') : 'Date not available'}
                        </h6>
                        <span className="score-label">Test Result</span>
                      </div>

                      <div className="score-visual">
                        <svg className="score-ring" viewBox="0 0 200 200">
                          <defs>
                            <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#3b82f6" />
                              <stop offset="100%" stopColor="#8b5cf6" />
                            </linearGradient>
                          </defs>
                          <circle className="score-ring-bg" cx="100" cy="100" r="85" fill="none" strokeWidth="10" />
                          <circle
                            className="score-ring-progress"
                            cx="100"
                            cy="100"
                            r="85"
                            fill="none"
                            strokeWidth="10"
                            strokeDasharray="534"
                            strokeDashoffset={calculateDashOffset(test.score)}
                            transform="rotate(-90 100 100)"
                          />
                        </svg>
                        <div className="score-value">
                          <span className="score-label">Your score</span>
                          <span className="score-number">{test.score !== null && test.score !== undefined ? test.score : '--'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Mobile slider controls */}
                <div className="score-slider-controls">
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => activeSlide > 0 && setActiveSlide(activeSlide - 1)}
                    disabled={activeSlide === 0}
                  >
                    ‹
                  </button>

                  <div className="dots">
                    {Array.from({ length: totalSlides }).map((_, i) => (
                      <span key={i} className={i === activeSlide ? 'active' : ''} onClick={() => setActiveSlide(i)} />
                    ))}
                  </div>

                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => activeSlide < totalSlides - 1 && setActiveSlide(activeSlide + 1)}
                    disabled={activeSlide === totalSlides - 1}
                  >
                    ›
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="info-card">
          <div className="info-card__container">
            {/* Left Content */}
            <div className="info-card__content">
              <h2 className="info-card__title">
                Why testing once <br /> isn’t enough
              </h2>

              <p className="info-card__description">
                A single test is only a snapshot. Semen quality shifts with stress, sleep, illness, and lifestyle changes. Regular testing shows real
                trends instead of temporary dips, giving you clearer guidance for the future. Your semen fully regenerates every 74-90 days. Track
                progress over time with an optional testing plan
              </p>

              <button className="info-card__cta btn btn-primary w-fit px-4">See all options</button>
            </div>

            {/* Right Image */}
            <div className="info-card__image-wrapper">
              <video className="test-video" loop autoPlay muted>
                <source src={TestHistoryVid} type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </div>
      {/* <Footer /> */}
    </div>
  );
};

export default TestHistory;
